import { useState } from "react";
import {
  Box,
  Heading,
  Text,
  Flex,
  Icon,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Spinner,
  useDisclosure,
  useToast,
  Select,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import {
  IoIosArrowBack,
  IoIosRadioButtonOn,
  IoIosRadioButtonOff,
} from "react-icons/io";
import { TiLocation } from "react-icons/ti";
import { IoChevronDown, IoGift } from "react-icons/io5";
import { RiCheckboxBlankLine, RiCheckboxFill } from "react-icons/ri";

import { PaymentDisclosure } from "../features/payment/PaymentDisclosure";
import { createBooking, fetchDiscountCoupon } from "../services/booking";

const depositTypes = [
  { id: 1, value: "Pay total amount", slug: "pay_online" },
  { id: 2, value: "Pay Deposit 50%", slug: "pay_at_venue" },
  { id: 3, value: "Pay Deposit 25%", slug: "pay_at_venue_25" },
];

export const FinalizeBooking = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    cart,
    name,
    date,
    startTime,
    selectedTeamMember,
    firstName,
    lastName,
  } = location.state || {};

  const [depositType, setDepositType] = useState<string>("");
  const [acceptTAndC, setAcceptTAndC] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [discountCode, setDiscountCode] = useState<string>("");
  const [discount, setDiscount] = useState<any>();
  const [expandedTAndC, setExpandedTAndC] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>("");
  const [additionalInformation, setAdditionalInformation] = useState<any>({
    address: "",
    parking: "",
    pets: "",
  });

  // Every cart item belongs to the same business (enforced when adding to the
  // cart), so the first item is authoritative.
  const businessId = cart[0]?.business_id;

  const paymentDisclosure = useDisclosure();
  const toast = useToast();

  const stripe: any = useStripe();
  const elements = useElements();

  const handlePayPress = async () => {
    setLoading(true);

    if (!stripe || !elements) {
      console.error("Stripe has not loaded yet.");
      return;
    }

    // Create a payment method using card details
    const cardElement = elements.getElement(CardElement);
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
    });

    if (error) {
      console.error(error);
    } else {
      const amount = cart.reduce(
        (accumulator: any, currentValue: { service: { price: any } }) =>
          accumulator + currentValue.service.price,
        0,
      );

      let discountedVal = 1;
      if (depositType === "pay_online") discountedVal = 1;
      else if (depositType === "pay_at_venue") discountedVal = 0.5;
      else if (depositType === "pay_at_venue_25") discountedVal = 0.25;

      const dayDate = new Date(date);
      const timeDate = new Date(`1970-01-01T${startTime}:00`);

      const combinedDate = new Date(
        dayDate.getFullYear(),
        dayDate.getMonth(),
        dayDate.getDate(),
        timeDate.getUTCHours(),
        timeDate.getUTCMinutes(),
        timeDate.getUTCSeconds(),
        timeDate.getUTCMilliseconds(),
      );

      // Services run back-to-back: each one starts when the previous ends.
      let slotCursor = combinedDate.getTime();

      const body = {
        business_id: businessId,
        booking_data: cart.map((item: any) => {
          const itemDuration = Number(
            item.finalDuration ?? item.service.duration_minute,
          );
          const slotStart = slotCursor;
          const slotEnd = slotStart + itemDuration * 60 * 1000;
          slotCursor = slotEnd;

          return {
            service_id: item.service._id,
            team_member_id: selectedTeamMember,
            slot_start_time: new Date(slotStart).toISOString(),
            slot_end_time: new Date(slotEnd).toISOString(),
            servicePrice: item.finalPrice,
            serviceDuration: itemDuration,
            // Only the groups actually chosen: the server ranks every key it
            // receives, and an empty value ranks as -1 rather than as "not set".
            serviceExtras: {
              ...(item.selectedLength ? { length: item.selectedLength } : {}),
              ...(item.selectedSize ? { size: item.selectedSize } : {}),
            },
          };
        }),
        gross_amount: amount,
        discount_amount: discount?.discount_amount || 0,
        net_total: amount,
        paid_amount: amount * discountedVal,
        discount_coupon_id: discount?.data?._id || "",
        // Only sent when the customer actually entered a code.
        ...(discountCode ? { discount_code: discountCode } : {}),
        isAgreeOnTerms: String(acceptTAndC),
        paymentMethod: depositType,
        stripePaymentMethodId: paymentMethod.id,
        name,
        additionalNotes: notes,
        additionalInformation,
        timezone: "Europe/London",
        platform: "booking-ui",
      };

      const res = await createBooking(body);

      if (res?.status === 200) {
        toast({
          title: "Success",
          description: res.message,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });

        paymentDisclosure.onClose();

        navigate("/success", {
          state: {
            firstName,
            lastName,
            serviceName: cart[0].service.name,
            amount: amount * discountedVal,
            date,
            startTime,
          },
        });
      } else {
        toast({
          title: "An error occcured",
          description: res?.message,
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      }
    }

    setLoading(false);
  };

  const applyDiscountCoupon = async () => {
    setLoading(true);

    if (discountCode) {
      const res = await fetchDiscountCoupon({
        coupon_code: discountCode,
        gross_amount: cart.reduce(
          (accumulator: any, currentValue: { service: { price: any } }) =>
            accumulator + currentValue.service.price,
          0,
        ),
        business_id: businessId,
      });

      if (res.status === 200) {
        setDiscount(res);

        toast({
          title: "Success",
          description: res.message,
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } else {
        toast({
          title: "An error occcured",
          description: res.message,
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      }
    }

    setLoading(false);
  };

  const yesNoOptions = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" },
  ];

  return (
    <Box pb="20px">
      <Flex
        justify={"space-between"}
        align={"center"}
        bg={"linear-gradient(to bottom right, #FFF7F3, #D8F1F2)"}
        borderBottomLeftRadius={24}
        borderBottomRightRadius={24}
        px={"20px"}
        pb={"20px"}
        pt={"54px"}
      >
        <Icon fontSize={24} color={"#19202D"} onClick={() => navigate(-1)}>
          <IoIosArrowBack />
        </Icon>

        <Heading fontSize={"20px"} textAlign={"center"} color={"#19202D"}>
          Finalise Booking
        </Heading>

        <Box w={"24px"} />
      </Flex>

      <Box pt="24px" px="20px">
        <Accordion allowMultiple>
          <AccordionItem
            borderTopWidth={0}
            borderBottomWidth={"0px !important"}
          >
            <Box
              p="16px"
              boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
              borderRadius={"30px"}
            >
              <AccordionButton p={0} bg="white" _hover={{ bg: "white" }}>
                <Box as="span" flex="1" textAlign="left">
                  <Heading
                    fontSize={"16px"}
                    color={"#19202D"}
                    fontFamily={"RedHatDisplay-Bold"}
                  >
                    Additional Information
                  </Heading>

                  <Text mt="8px" color={"#4F4F51"}>
                    For mobile services, we require additional information about
                    your location.
                  </Text>
                </Box>
                <AccordionIcon boxSize={"30px"} />
              </AccordionButton>

              <AccordionPanel p={0} pb="2px">
                <Box mt="32px">
                  <Heading
                    fontSize={"16px"}
                    color={"#19202D"}
                    mb="8px"
                    fontFamily={"RedHatDisplay-Bold"}
                  >
                    Your Address (for mobile service)
                  </Heading>

                  <InputGroup
                    borderRadius={"40px"}
                    h="56px"
                    alignItems={"center"}
                    boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
                  >
                    <InputLeftElement pointerEvents="none" h="56px" pl="16px">
                      <Icon fontSize={24} color={"#9BA3A3"}>
                        <TiLocation />
                      </Icon>
                    </InputLeftElement>

                    <Input
                      placeholder="Your Address (for mobile services)"
                      borderWidth={0}
                      fontSize={"16px"}
                      ml="8px"
                      _focusVisible={{ borderColor: "none" }}
                      value={additionalInformation?.address}
                      onChange={(e) =>
                        setAdditionalInformation({
                          ...additionalInformation,
                          address: e.target.value,
                        })
                      }
                    />
                  </InputGroup>
                </Box>

                <Box mt="20px">
                  <Heading
                    fontSize={"16px"}
                    color={"#19202D"}
                    mb="8px"
                    fontFamily={"RedHatDisplay-Bold"}
                  >
                    Is there parking available?
                  </Heading>

                  <InputGroup
                    borderRadius={"40px"}
                    h="56px"
                    alignItems={"center"}
                    boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
                    my="8px"
                    // color={"#9BA3A3"}
                  >
                    <Select
                      placeholder="Is there parking available?"
                      fontSize={"16px"}
                      border={0}
                      icon={
                        <Icon fontSize={24} color={"#19202D"} mr="16px">
                          <IoChevronDown />
                        </Icon>
                      }
                      _focusVisible={{ borderColor: "none" }}
                      value={additionalInformation?.parking}
                      onChange={(e) =>
                        setAdditionalInformation({
                          ...additionalInformation,
                          parking: e.target.value,
                        })
                      }
                    >
                      {yesNoOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </InputGroup>
                </Box>

                <Box mt="20px">
                  <Heading
                    fontSize={"16px"}
                    color={"#19202D"}
                    mb="8px"
                    fontFamily={"RedHatDisplay-Bold"}
                  >
                    Do you have any pets?
                  </Heading>

                  <InputGroup
                    borderRadius={"40px"}
                    h="56px"
                    alignItems={"center"}
                    boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
                    my="8px"
                  >
                    <Select
                      placeholder="Do you have any pets?"
                      border={0}
                      fontSize={"16px"}
                      icon={
                        <Icon fontSize={24} color={"#19202D"} mr="16px">
                          <IoChevronDown />
                        </Icon>
                      }
                      _focusVisible={{ borderColor: "none" }}
                      value={additionalInformation?.pets}
                      onChange={(e) =>
                        setAdditionalInformation({
                          ...additionalInformation,
                          pets: e.target.value,
                        })
                      }
                    >
                      {yesNoOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </InputGroup>
                </Box>
              </AccordionPanel>
            </Box>
          </AccordionItem>
        </Accordion>

        {/* <Box mt="20px">
          <Heading
            fontSize={"16px"}
            color={"#19202D"}
            mb="8px"
            fontFamily={"RedHatDisplay-Bold"}
          >
            Where did you hear about me?
          </Heading>

          <InputGroup
            borderRadius={"40px"}
            h="56px"
            alignItems={"center"}
            boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
          >
            <Input
              placeholder="Please Select"
              color="#9BA3A3"
              borderWidth={0}
              fontSize={"16px"}
            />

            <InputRightElement pointerEvents="none" h="56px" pr="16px">
              <Icon fontSize={24} color={"#19202D"}>
                <IoChevronDown />
              </Icon>
            </InputRightElement>
          </InputGroup>
        </Box> */}

        <Box my="20px">
          <Heading
            fontSize={"16px"}
            color={"#19202D"}
            mb="8px"
            fontFamily={"RedHatDisplay-Bold"}
          >
            Any additional notes for your Book-d Pro?
          </Heading>

          <InputGroup
            borderRadius={"40px"}
            h="56px"
            alignItems={"center"}
            boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
          >
            <Input
              placeholder="Notes"
              borderWidth={0}
              fontSize={"16px"}
              _focusVisible={{ borderColor: "none" }}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </InputGroup>
        </Box>

        <Box>
          <Heading fontSize={"18px"} color={"#19202D"}>
            Terms & Conditions
          </Heading>

          <Text
            fontSize={"14px"}
            mt="7px"
            color={"#4F4F51"}
            noOfLines={expandedTAndC ? undefined : 3}
          >
            By booking an appointment with me, you agree to the following Terms
            and Conditions: Appointment Cancellations: Please provide at least
            24 hours' notice for any cancellations or rescheduling. Late
            Arrivals: If you arrive late, your appointment may need to be
            shortened to accommodate the next client. Payment: Payment is due at
            the time of service, either by cash or card. No-Show Policy: Failure
            to show up for your appointment without prior notice may result in a
            cancellation fee. Satisfaction Guarantee: If you're not satisfied
            with your service, please let me know immediately so I can make it
            right. Thank you for your understanding and cooperation. I look
            forward to providing you with excellent service!"
          </Text>

          {
            <Text
              fontSize={"14px"}
              mt="7px"
              color={"#4F4F51"}
              cursor={"pointer"}
              onClick={() => setExpandedTAndC((prev) => !prev)}
              textDecoration={"underline"}
              fontWeight={600}
            >
              Read {!expandedTAndC ? "More" : "Less"}...
            </Text>
          }
        </Box>

        <Flex mt="16px" gap={"8px"}>
          <Icon
            fontSize={"24px"}
            color={"#FF8E3C"}
            onClick={() => setAcceptTAndC((prev) => !prev)}
          >
            {acceptTAndC ? <RiCheckboxFill /> : <RiCheckboxBlankLine />}
          </Icon>

          <Text fontSize={"14px"} color={"#4F4F51"}>
            By checking this, I confirm that I have read and agree to the Terms
            and Conditions outlined above and Book-d Terms of Service.
          </Text>
        </Flex>

        <Box mt="20px">
          <Heading fontSize={"18px"} color={"#19202D"} mb="8px">
            Payment Methods
          </Heading>

          {depositTypes.map((_depositType) => (
            <Flex
              gap="8px"
              mt="16px"
              key={_depositType.id}
              onClick={() => setDepositType(_depositType.slug)}
            >
              <Icon fontSize={"24px"} color={"#FF8E3C"}>
                {depositType === _depositType.slug ? (
                  <IoIosRadioButtonOn />
                ) : (
                  <IoIosRadioButtonOff />
                )}
              </Icon>

              <Flex align={"center"} gap={"6px"}>
                <Text fontSize={"16px"} color={"#4F4F51"} lineHeight={"24px"}>
                  {_depositType.value}
                </Text>
              </Flex>
            </Flex>
          ))}
        </Box>

        <Box mt="32px">
          <Flex justify={"space-between"} align={"center"} mb="8px">
            <Heading
              fontSize={"16px"}
              color={"#19202D"}
              mb="8px"
              fontFamily={"RedHatDisplay-Bold"}
            >
              Discount Code
            </Heading>

            {loading ? (
              <Spinner color="#FF8E3C" />
            ) : (
              <Text
                fontSize={"14px"}
                fontWeight={500}
                color={"#267684"}
                cursor={"pointer"}
                onClick={applyDiscountCoupon}
              >
                Apply
              </Text>
            )}
          </Flex>

          <InputGroup
            borderRadius={"40px"}
            h="56px"
            alignItems={"center"}
            boxShadow="0px 2px 5px rgba(0, 0, 0, 0.1)"
          >
            <InputLeftElement pointerEvents="none" h="56px" pl="16px">
              <Icon fontSize={24} color={"#9BA3A3"}>
                <IoGift />
              </Icon>
            </InputLeftElement>

            <Input
              placeholder="** ** **"
              color="#9BA3A3"
              borderWidth={0}
              fontSize={"16px"}
              ml="8px"
              _focusVisible={{ borderColor: "none" }}
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
            />
          </InputGroup>
        </Box>

        <Button
          bg={"#FF8E3C"}
          _hover={{ bg: "#FF8E3C" }}
          mt="24px"
          borderRadius={"30px"}
          color={"white"}
          w="100%"
          h="58px"
          fontSize={"17px"}
          fontFamily={"RedHatDisplay-Bold"}
          disabled={!depositType || !acceptTAndC}
          onClick={() => paymentDisclosure.onOpen()}
        >
          Pay and Book
        </Button>
      </Box>

      <PaymentDisclosure
        paymentDisclosure={paymentDisclosure}
        loading={loading}
        handlePayPress={handlePayPress}
        CardElement={CardElement}
      />
    </Box>
  );
};
